"use client";

import { useCallback, useEffect, useLayoutEffect, useRef, useState, type AnimationEvent, type ComponentProps } from "react";
import { createPortal } from "react-dom";
import Image from "next/image";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight, MapPin, Orbit, Scaling, X } from "lucide-react";

import { type Project } from "@/entities/project";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Reveal } from "@/shared/ui/reveal";
import { Section, SectionHeading } from "@/shared/ui/section";
import type { HomeContent } from "@/shared/config/home";
import { cn } from "cn";
import styles from "./object-viewer.module.css";
import { ModelLoader, SplatSurface } from "@/shared/ui/splat-viewer";
import type { SceneLoadProgress, ViewerController } from "@/shared/config/splat-scene";
import carousel from "./case-carousel.module.css";

function NativeScene({ slug, register, registerController, ready, failed, returned, ...props }: ComponentProps<typeof SplatSurface> & {
  slug: string;
  register: (slug: string, node: HTMLDivElement | null) => void;
  registerController: (slug: string, api: ViewerController | null) => void;
  ready: (slug: string) => void;
  failed: (slug: string) => void;
  returned: () => void;
}) {
  const node = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => { register(slug, node.current); return () => register(slug, null); }, [slug, register]);
  return <SplatSurface {...props} hostRef={element => { node.current = element; }}
    onController={api => registerController(slug, api)} onReady={() => ready(slug)}
    onError={() => failed(slug)} onReturnReady={returned} />;
}

// Independent viewers can briefly overlap while an old object returns to its card.
const scrollOwners = new Set<symbol>();
let latestViewerRequest: symbol | null = null;
let originalOverflow = "";
function lockPageScroll(owner: symbol) {
  if (!scrollOwners.size) originalOverflow = document.body.style.overflow;
  scrollOwners.add(owner);
  document.body.style.overflow = "hidden";
}
function unlockPageScroll(owner: symbol) {
  if (scrollOwners.delete(owner) && !scrollOwners.size) document.body.style.overflow = originalOverflow;
}

/** Декоративная сетка точек для обложки без фотографии. */
const dotGridStyle = {
  backgroundImage:
    "radial-gradient(color-mix(in oklab, var(--primary) 32%, transparent) 1px, transparent 1px)",
  backgroundSize: "22px 22px",
};

/**
 * Обложка кейса: фотография, когда она появится, либо фирменный
 * градиент с «призрачным» глифом орбиты — карточка не должна ломаться без image.
 */
function CaseCover({ project }: { project: Project }) {
  if (project.image) {
    if (project.imageSrcSet) return (
      // These persistent WebP variants are already sized and encoded on upload.
      // eslint-disable-next-line @next/next/no-img-element
      <img src={project.image} srcSet={project.imageSrcSet} sizes="(min-width: 768px) 50vw, 100vw"
        alt={project.title} width={800} height={600} draggable={false} loading="lazy" decoding="async"
        className="absolute inset-0 h-full w-full object-cover" />
    );
    return (
      <Image
        src={project.image}
        alt={project.title}
        fill
        draggable={false}
        quality={project.viewerMode === 'object' ? 95 : 75}
        sizes="(min-width: 768px) 50vw, 100vw"
        className={cn("object-cover", project.viewerMode !== "object" && "transition-transform duration-700 ease-out group-hover:scale-[1.03]")}
      />
    );
  }

  return (
    <div
      aria-hidden
      style={dotGridStyle}
      className={cn(
        "flex h-full w-full items-center justify-center bg-gradient-to-br transition-transform duration-700 ease-out group-hover:scale-[1.03]",
        project.accent,
      )}
    >
      {/* Свечение-«сплат» в стиле hero, чтобы обложка читалась как арт, а не пустота */}
      <span
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(62% 56% at 32% 30%, color-mix(in oklab, var(--primary) 30%, transparent), transparent 72%)",
        }}
      />
      <Orbit size={48} className="relative text-primary/60" />
    </div>
  );
}

function setObjectOrigin(dialog: HTMLDialogElement, trigger: HTMLElement) {
  const rect = (trigger.querySelector<HTMLElement>('[data-case-cover] img') ?? trigger).getBoundingClientRect();
  for (const [name, value] of Object.entries({
    'reveal-left': rect.left, 'reveal-top': rect.top,
    'reveal-width': rect.width, 'reveal-height': rect.height,
  })) dialog.style.setProperty(`--object-${name}`, `${value}px`);
}

function CaseCard({ project: sourceProject }: { project: Project }) {
  const slides = [sourceProject, ...(sourceProject.variants ?? [])];
  const [activeIndex, setActiveIndex] = useState(0);
  const project = slides[activeIndex];
  const activeSlug = project.slug;
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start", loop: false, duration: 28, dragThreshold: 8,
    active: slides.length > 1,
    breakpoints: { "(prefers-reduced-motion: reduce)": { duration: 0 } },
  });
  const selectSlide = (index: number) => emblaApi?.scrollTo(index);
  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setActiveIndex(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect).on("reInit", onSelect);
    return () => { emblaApi.off("select", onSelect).off("reInit", onSelect); };
  }, [emblaApi]);
  const isObject = project.viewerMode === "object";
  const viewerTitle = `${isObject ? "3D-модель" : "3D-прогулка"} «${project.title}»`;
  const surfaceRef = useRef<HTMLDivElement | HTMLIFrameElement | null>(null);
  const controllers = useRef(new Map<string, ViewerController>());
  const [loadState, setLoadState] = useState<"loading" | "ready" | "error">("loading");
  const [progressBySlug, setProgressBySlug] = useState(new Map<string, SceneLoadProgress>());
  const [readyModels, setReadyModels] = useState(new Set<string>());
  const [failedModels, setFailedModels] = useState(new Set<string>());
  const scrollOwner = useRef(Symbol('case-viewer'));
  const requestedScene = useRef<{ slug: string; trigger: HTMLElement; project: Project } | null>(null);
  const loadTimeout = useRef<number | null>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const [portalHost, setPortalHost] = useState<HTMLElement | null>(null);
  useEffect(() => {
    const frame = requestAnimationFrame(() => setPortalHost(document.body));
    return () => cancelAnimationFrame(frame);
  }, []);
  const fullscreenRef = useRef<HTMLDivElement>(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [returned, setReturned] = useState(false);
  const cacheable = Boolean(project.sceneEndpoint);
  const [mountedModels, setMountedModels] = useState<Set<string>>(() => new Set());
  const cachedFrames = useRef(new Map<string, HTMLDivElement | HTMLIFrameElement>());
  const selectedSlug = useRef(activeSlug);
  useLayoutEffect(() => {
    selectedSlug.current = activeSlug;
    surfaceRef.current = cachedFrames.current.get(activeSlug) ?? null;
  }, [activeSlug, mountedModels]);
  const registerFrame = useCallback((slug: string, node: HTMLDivElement | HTMLIFrameElement | null) => {
    const old = cachedFrames.current.get(slug);
    if (node) { cachedFrames.current.set(slug, node); if (slug === selectedSlug.current) surfaceRef.current = node; }
    else { cachedFrames.current.delete(slug); if (surfaceRef.current === old) surfaceRef.current = null; }
  }, []);
  const registerController = useCallback((slug: string, api: ViewerController | null) => {
    if (api) controllers.current.set(slug, api); else controllers.current.delete(slug);
  }, []);
  const sceneReady = useCallback((slug: string) => {
    setReadyModels(models => new Set([...models, slug]));
    const request = requestedScene.current;
    const dialog = dialogRef.current;
    const api = controllers.current.get(slug);
    if (!request || request.slug !== slug || !dialog || !api) return;
    if (latestViewerRequest !== scrollOwner.current || selectedSlug.current !== slug) {
      requestedScene.current = null;
      setViewerOpen(false);
      api.suspend();
      return;
    }
    // The page may have scrolled since the click. Start from the live crop.
    if (request.project.viewerMode === 'object') setObjectOrigin(dialog, request.trigger);
    if (!dialog.open) {
      if (request.project.viewerMode === 'object') dialog.show(); else dialog.showModal();
    }
    api.emerge();
    void api.resume().then(() => {
      if (requestedScene.current !== request || latestViewerRequest !== scrollOwner.current || selectedSlug.current !== slug) {
        api.suspend();
        dialog.close();
        return;
      }
      setLoadState('ready');
    }).catch(() => setLoadState('error'));
  }, []);
  const sceneFailed = useCallback((slug: string) => {
    setFailedModels(models => new Set([...models, slug]));
    if (slug === selectedSlug.current) setLoadState('error');
  }, []);
  const sceneProgress = useCallback((slug: string, progress: SceneLoadProgress) => {
    setProgressBySlug(values => new Map(values).set(slug, progress));
    if (slug === requestedScene.current?.slug) {
      // A slow transfer is still healthy while bytes continue arriving.
      if (loadTimeout.current !== null) window.clearTimeout(loadTimeout.current);
      loadTimeout.current = window.setTimeout(() => setLoadState(state => state === 'loading' ? 'error' : state), 45000);
    }
  }, []);
  const resumeAnimations = useRef<(() => void) | null>(null);
  const returnFrame = useRef<number | null>(null);
  const geometryReady = useRef(false);
  const returnRendered = useRef(false);
  const returnedRef = useRef(false);
  const fadeAnimation = useRef<Animation | null>(null);
  const closingRef = useRef(false);
  const closeTimeoutRef = useRef<number | null>(null);
  const finishClose = useCallback(() => {
    requestedScene.current = null;
    const wasReturning = closingRef.current;
    if (returnFrame.current !== null) cancelAnimationFrame(returnFrame.current);
    if (closeTimeoutRef.current !== null) window.clearTimeout(closeTimeoutRef.current);
    unlockPageScroll(scrollOwner.current);
    resumeAnimations.current?.();
    controllers.current.get(selectedSlug.current)?.suspend();
    if (document.fullscreenElement === fullscreenRef.current) void document.exitFullscreen();
    dialogRef.current?.close();
    closingRef.current = false;
    returnedRef.current = false;
    setReturned(false);
    setClosing(false);
    setViewerOpen(false);
    if (!wasReturning) triggerRef.current?.focus({ preventScroll: true });
  }, []);
  const completeReturn = useCallback(() => {
    if (closingRef.current && geometryReady.current && returnRendered.current && !returnedRef.current) {
      returnedRef.current = true;
      setReturned(true);
    }
  }, []);
  const sceneReturned = useCallback(() => { returnRendered.current = true; completeReturn(); }, [completeReturn]);
  const sceneEmerge = useCallback((event: AnimationEvent<HTMLDivElement>) => {
    // Annotation fades bubble animationstart too; only the viewer host opens
    // the camera. Otherwise the delayed marker fade restarts the whole reveal.
    if (event.target !== event.currentTarget || closingRef.current) return;
    controllers.current.get(selectedSlug.current)?.emerge();
  }, []);
  useLayoutEffect(() => {
    if (!returned) return;
    // The restored photo is committed beneath the model before its 100 ms fade starts.
    const frame = requestAnimationFrame(() => {
      const surface = surfaceRef.current;
      if (!surface) { finishClose(); return; }
      fadeAnimation.current = surface.animate([{ opacity: getComputedStyle(surface).opacity }, { opacity: 0 }], { duration: 100, easing: "linear", fill: "forwards" });
      if (closeTimeoutRef.current !== null) window.clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = window.setTimeout(finishClose, 100);
    });
    return () => { cancelAnimationFrame(frame); fadeAnimation.current?.cancel(); };
  }, [returned, finishClose]);
  const closeViewer = useCallback(() => {
    if (closingRef.current) return;
    const surface = surfaceRef.current;
    const dialog = dialogRef.current;
    if (!isObject || !surface || !dialog || surface.dataset.ready !== "true" || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      finishClose(); return;
    }
    const start = surface.getBoundingClientRect();
    const opacity = getComputedStyle(surface).opacity;
    surface.style.animation = "none";
    surface.getAnimations().forEach(animation => animation.cancel());
    Object.assign(surface.style, { left: `${start.left}px`, top: `${start.top}px`, width: `${start.width}px`, height: `${start.height}px`, opacity, clipPath: 'inset(0px)' });
    closingRef.current = true;
    dialog.style.pointerEvents = "none";
    surface.style.pointerEvents = "none";
    geometryReady.current = false;
    returnRendered.current = false;
    returnedRef.current = false;
    setClosing(true);
    unlockPageScroll(scrollOwner.current);
    resumeAnimations.current?.();
    triggerRef.current?.focus({ preventScroll: true });
    controllers.current.get(selectedSlug.current)?.returnHome();
    const begun = performance.now();
    let waitingForGeometry = false;
    const update = () => {
      if (!closingRef.current) return;
      const photo = triggerRef.current?.querySelector<HTMLElement>("[data-case-cover] img");
      if (!photo) { finishClose(); return; }
      // Follow the live card, including scrolling while the object returns.
      const rect = photo.getBoundingClientRect();
      const progress = Math.min(1, (performance.now() - begun) / 700);
      const ease = progress * progress * (3 - 2 * progress);
      surface.style.left = `${start.left + (rect.left - start.left) * ease}px`;
      surface.style.top = `${start.top + (rect.top - start.top) * ease}px`;
      surface.style.width = `${start.width + (rect.width - start.width) * ease}px`;
      surface.style.height = `${start.height + (rect.height - start.height) * ease}px`;
      if (progress === 1 && !waitingForGeometry) {
        waitingForGeometry = true;
        // Render at the final crop size before restoring the photo beneath it.
        const rendered = controllers.current.get(selectedSlug.current)?.resume() ?? Promise.resolve();
        void rendered.then(() => { geometryReady.current = true; completeReturn(); }).catch(finishClose);
      }
      returnFrame.current = requestAnimationFrame(update);
    };
    returnFrame.current = requestAnimationFrame(update);
    // Only a failed renderer uses this fallback; normal closing waits for its final frame.
    closeTimeoutRef.current = window.setTimeout(finishClose, 2500);
  }, [finishClose, isObject, completeReturn]);
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const onCancel = (event: Event) => {
      event.preventDefault();
      closeViewer();
    };
    const onFullscreenChange = () => {
      if (!isObject && !document.fullscreenElement && dialog.open) {
        dialog.close();
        setViewerOpen(false);
      }
    };
    const onEscape = (event: KeyboardEvent) => {
      if (isObject && dialog.open && event.key === "Escape") { event.preventDefault(); closeViewer(); }
    };
    const onFocus = (event: FocusEvent) => {
      if (isObject && dialog.open && !closingRef.current && event.target instanceof Node && !dialog.contains(event.target)) {
        dialog.querySelector<HTMLCanvasElement>('[data-ready="true"] canvas')?.focus({ preventScroll: true });
      }
    };
    window.addEventListener("keydown", onEscape);
    document.addEventListener("focusin", onFocus);
    dialog.addEventListener("cancel", onCancel);
    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () => {
      window.removeEventListener("keydown", onEscape);
      document.removeEventListener("focusin", onFocus);
      dialog.removeEventListener("cancel", onCancel);
      document.removeEventListener("fullscreenchange", onFullscreenChange);
    };
  }, [isObject, closeViewer, portalHost]);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      const frame = surfaceRef.current;
      if (!(frame instanceof HTMLIFrameElement) || event.source !== frame.contentWindow || event.origin !== location.origin) return;
      if (event.data?.type === 'splat-viewer:ready') setLoadState('ready');
      if (event.data?.type === 'splat-viewer:error') setLoadState('error');
      if (event.data?.type === 'splat-viewer:close') closeViewer();
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [closeViewer]);
  useLayoutEffect(() => {
    if (!viewerOpen || (cacheable && loadState !== 'ready')) return;
    const owner = scrollOwner.current;
    lockPageScroll(owner);
    const animations = document.getAnimations().filter(animation => {
      const target = animation.effect instanceof KeyframeEffect ? animation.effect.target : null;
      return target instanceof Element && !target.closest("dialog, [data-case-cover]") && animation.playState === "running";
    });
    animations.forEach(animation => animation.pause());
    let resumed = false;
    const resume = () => {
      if (resumed) return;
      resumed = true;
      animations.forEach(animation => { if (animation.playState === "paused") animation.play(); });
    };
    resumeAnimations.current = resume;
    return () => { unlockPageScroll(owner); resume(); };
  }, [viewerOpen, cacheable, loadState]);
  useEffect(() => {
    if (!viewerOpen) return;
    loadTimeout.current = window.setTimeout(() => setLoadState(state => state === "loading" ? "error" : state), 45000);
    const api = controllers.current.get(project.slug);
    if (cacheable && api) {
      sceneReady(project.slug);
    }
    return () => {
      if (loadTimeout.current !== null) window.clearTimeout(loadTimeout.current);
      if (closeTimeoutRef.current !== null) window.clearTimeout(closeTimeoutRef.current);
      if (returnFrame.current !== null) cancelAnimationFrame(returnFrame.current);
    };
  }, [viewerOpen, project.slug, project.viewerUrl, cacheable, sceneReady, sceneFailed]);

  const openViewer = (trigger: HTMLElement) => {
    const dialog = dialogRef.current;
    if (!dialog || closingRef.current) return;
    latestViewerRequest = scrollOwner.current;
    requestedScene.current = { slug: project.slug, trigger, project };
    if (isObject) setObjectOrigin(dialog, trigger);
    const cached = cachedFrames.current.get(project.slug);
    if (cached) { cached.getAnimations().forEach(animation => animation.cancel()); cached.removeAttribute("style"); }
    if (cacheable) setMountedModels(models => new Set([...models, project.slug]));
    returnedRef.current = false;
    setReturned(false);
    closingRef.current = false;
    setClosing(false);
    setLoadState("loading");
    triggerRef.current = trigger;
    dialog.style.pointerEvents = "";
    if (!cacheable) dialog.showModal();
    setViewerOpen(true);
    if (!isObject && !cacheable && fullscreenRef.current?.requestFullscreen) void fullscreenRef.current.requestFullscreen().catch(() => undefined);
  };
  const renderCard = (project: Project) => (
    <>
      <div data-case-cover data-preview-hidden={isObject && viewerOpen && loadState === "ready" && !returned && project.slug === slides[activeIndex].slug}
        data-preview-returning={closing && project.slug === slides[activeIndex].slug}
        className={cn(styles.cover, "relative m-2.5 mb-0 aspect-[4/3] overflow-hidden rounded-2xl md:m-3 md:mb-0", !isObject && "border border-border")}>
        <CaseCover project={project} />
        {project.sceneEndpoint && mountedModels.has(project.slug) && !readyModels.has(project.slug) && !failedModels.has(project.slug) &&
          <div className={styles.cardLoading}><ModelLoader progress={progressBySlug.get(project.slug)} /></div>}
        {failedModels.has(project.slug) && <div className={styles.status} role="alert">Не удалось загрузить модель.</div>}
        {project.viewerUrl && <span className="absolute bottom-3 right-3 rounded-full bg-background/85 px-3 py-1.5 text-xs font-medium text-foreground">{isObject ? "Рассмотреть в 3D" : "Открыть прогулку"}</span>}
      </div>

      <div className="flex flex-1 flex-col gap-3 p-5 md:p-6">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <Badge variant="secondary">{project.category}</Badge>
          {project.location && <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
            <MapPin size={14} aria-hidden />
            {project.location}
          </span>}
          {project.area ? (
            <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
              <Scaling size={14} aria-hidden />
              {project.area}
            </span>
          ) : null}
        </div>

        <h3 className="font-heading text-xl font-medium leading-snug">
          {project.title}
        </h3>

        <p className="line-clamp-3 text-sm leading-relaxed text-muted-foreground">
          {project.description}
        </p>

        <div className="mt-auto flex flex-wrap items-center gap-2 pt-2">
          {project.deliverables.map((deliverable) => (
            <span
              key={deliverable}
              className="rounded-full border border-border px-2.5 py-0.5 text-xs text-muted-foreground"
            >
              {deliverable}
            </span>
          ))}
        </div>
      </div>
    </>
  );
  const cardClass = "group flex h-full w-full flex-col overflow-hidden rounded-3xl border border-border bg-card/40 text-left transition-colors duration-300 hover:border-primary/30 focus-visible:border-primary/30";
  if (!project.viewerUrl) {
    return <a href="#contact" aria-label={`Кейс «${project.title}» — обсудить похожий проект`} className={cardClass}>{renderCard(project)}</a>;
  }
  const renderViewerFrame = (slide: Project, cached: boolean) => {
    const active = viewerOpen && slide.slug === project.slug;
    const frameProps = {
      hidden: !slide.sceneEndpoint && cached && !active,
      'data-parked': cached && !active,
      'data-closing': closing && slide.slug === project.slug,
      'data-returned': returned && slide.slug === project.slug,
      'data-ready': active && loadState === 'ready',
      className: cn(slide.viewerMode === 'object' ? styles.model : 'absolute inset-0 h-full w-full border-0', slide.sceneEndpoint && styles.cachedModel),
    };
    if (slide.sceneEndpoint) return <NativeScene key={slide.slug} {...frameProps}
      endpoint={slide.sceneEndpoint} suspended={!active} slug={slide.slug} unclipped={slide.viewerMode === 'object'}
      register={registerFrame} registerController={registerController} ready={sceneReady} failed={sceneFailed}
      onProgress={progress => sceneProgress(slide.slug, progress)}
      returned={sceneReturned} onClose={closeViewer} onAnimationStart={sceneEmerge} />;
    if (slide.viewerMode === 'object') return null;
    return <iframe key={slide.slug} {...frameProps} ref={node => registerFrame(slide.slug, node)}
      title={`3D-прогулка «${slide.title}»`} src={slide.viewerUrl} allow="fullscreen; xr-spatial-tracking" />;
  };
  const viewerDialog = (
    <dialog ref={dialogRef} onClose={() => { if (dialogRef.current?.open) return; requestedScene.current = null; closingRef.current = false; setClosing(false); setViewerOpen(false); }} aria-label={viewerTitle} data-closing={closing}
      className={cn(isObject ? styles.dialog : "fixed inset-0 m-0 h-dvh max-h-none w-screen max-w-none overflow-hidden bg-background p-0 text-foreground backdrop:bg-black/95", (cacheable || mountedModels.size > 0) && styles.cacheDialog)}>
      <div ref={fullscreenRef} className={isObject ? styles.stage : "relative h-full w-full bg-background"}
        onClick={(event) => { if (isObject && event.target === event.currentTarget) closeViewer(); }}>
        {slides.filter(slide => slide.sceneEndpoint && mountedModels.has(slide.slug)).map(slide => renderViewerFrame(slide, true))}
        {!cacheable && viewerOpen && renderViewerFrame(project, false)}
        {(isObject || cacheable) && loadState === "error" && <div className={styles.status} role="alert">Не удалось загрузить модель. Закройте и попробуйте ещё раз.</div>}
        {!isObject && <button type="button" onClick={closeViewer} aria-label="Закрыть 3D-прогулку"
          className="absolute right-4 top-4 z-10 rounded-full border border-white/30 bg-black/70 p-2 text-white hover:bg-black focus-visible:outline-2 focus-visible:outline-white">
          <X size={22} aria-hidden />
        </button>}
      </div>
    </dialog>
  );
  return <>
    {slides.length > 1 ? <div className={cn(cardClass, carousel.carousel, closing && styles.returningCard)} role="group" aria-roledescription="карусель" aria-label="Примеры предметной съёмки">
      <div ref={emblaRef} className={carousel.viewport}
        onKeyDown={(event) => {
          if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
          event.preventDefault();
          selectSlide(event.key === "Home" ? 0 : event.key === "End" ? slides.length - 1 : activeIndex + (event.key === "ArrowRight" ? 1 : -1));
        }}>
        <div className={carousel.track}>
        {slides.map((slide, index) => <button key={slide.slug} type="button" className={cn("group", styles.trigger, carousel.slide)}
          tabIndex={index === activeIndex ? 0 : -1}
          aria-hidden={index !== activeIndex}
          disabled={!portalHost} aria-label={`Открыть 3D-модель «${slide.title}»`}
          onClick={(event) => { setActiveIndex(index); openViewer(event.currentTarget); }}>
          {renderCard(slide)}
        </button>)}
        </div>
      </div>
      <div className={carousel.controls}>
        <button type="button" className={carousel.arrow} disabled={activeIndex === 0} onClick={() => selectSlide(activeIndex - 1)} aria-label="Предыдущая модель"><ChevronLeft size={18} /></button>
        <div className={carousel.pagination}>
          {slides.map((slide, index) => <button key={slide.slug} type="button" onClick={() => selectSlide(index)}
            aria-label={`Показать: ${slide.title}`} aria-current={index === activeIndex ? "true" : undefined}
            className={carousel.dot}><span /></button>)}
          <span className={carousel.counter} aria-live="polite" aria-atomic="true">{String(activeIndex + 1).padStart(2, "0")} / {String(slides.length).padStart(2, "0")}</span>
        </div>
        <button type="button" className={carousel.arrow} disabled={activeIndex === slides.length - 1} onClick={() => selectSlide(activeIndex + 1)} aria-label="Следующая модель"><ChevronRight size={18} /></button>
      </div>
    </div> : <button type="button" disabled={!portalHost} onClick={(event) => openViewer(event.currentTarget)} aria-label={`Открыть ${isObject ? "3D-модель" : "3D-прогулку"} «${project.title}»`} className={cn(cardClass, styles.trigger, closing && styles.returningCard)}>{renderCard(project)}</button>}
    {portalHost && createPortal(viewerDialog, portalHost)}
  </>;
}

/** Секция кейсов: грид карточек с каскадным появлением. */
export function Cases({ items: projects, heading }: { items: Project[]; heading: HomeContent["cases"] }) {
  return (
    <Section id="cases">
      <SectionHeading {...heading} />

      <div className="mt-10 grid grid-cols-1 gap-5 md:mt-14 md:grid-cols-2 md:gap-6">
        {projects.map((project, index) => (
          <Reveal key={project.slug} className="min-w-0" delay={index * 0.1}>
            <CaseCard project={project} />
          </Reveal>
        ))}
      </div>

      <Reveal
        delay={0.15}
        className="mt-10 flex flex-col items-center gap-4 text-center md:mt-14"
      >
        <p className="text-lg text-muted-foreground">Хотите так же?</p>
        <Button asChild variant="outline" size="lg">
          <a href="#contact">Обсудить проект</a>
        </Button>
      </Reveal>
    </Section>
  );
}
